import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FiChevronDown, FiChevronRight, FiDownload, FiPrinter, FiRefreshCw } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import useVerifyAccess from 'hooks/useVerifyAccess';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import {
  getEmployeeProductSalesReport, getEmployeeProductSalesExport,
  getProductSalesReport, getProductSalesExport,
} from 'services/retail-report.service';
import { ReportContextBar, ReportPageHeader, ReportPageLayout, ReportState, ReportTableToolbar } from './ReportComponents';
import ProductSalesReportFilters from './ProductSalesReportFilters';
import {
  PRODUCT_SALES_METRICS, buildProductSalesWorkbook, employeeLabel,
  formatProductSalesMetric, formatProductSalesNumber, getProductSalesQuery, getProductSalesDiagnostics,
} from './productSalesReportData';
import './retailReports.scss';
import './productSalesReports.scss';

const MetricHeaders = () => PRODUCT_SALES_METRICS.map((metric) =>
  <th scope='col' className='is-number' key={metric.key}>{metric.label}</th>);
const MetricCells = ({ row }) => PRODUCT_SALES_METRICS.map((metric) =>
  <td className='is-number' key={metric.key}>{formatProductSalesMetric(row, metric)}</td>);

const ReportDiagnostics = ({ meta }) => {
  const diagnostics = getProductSalesDiagnostics(meta);
  return diagnostics.length ? <section className='bc_ps_diagnostics' role='note' aria-label='Đối chiếu số liệu'>
    {diagnostics.map((diagnostic) => <p key={diagnostic}>{diagnostic}</p>)}
  </section> : null;
};

const ProductTable = ({ items = [], summary, caption }) => <div className='bc_ps_table_wrap'>
  <table className='bc_ps_table'>
    <caption>{caption}</caption>
    <thead><tr><th scope='col'>Mã hàng</th><th scope='col'>Tên hàng</th><th scope='col'>ĐVT</th><MetricHeaders /></tr></thead>
    <tbody>{summary ? <tr className='bc_ps_total_row'><th scope='row' colSpan={3}>Tổng cộng toàn bộ kết quả lọc</th><MetricCells row={summary} /></tr> : null}{items.map((product) => <tr key={product.product_key}>
      <th scope='row' className='is-code'>{product.product_code || '—'}</th>
      <td className='is-product'>{product.product_name || 'Chưa xác định hàng hóa'}</td>
      <td>{product.unit_name || '—'}</td><MetricCells row={product} />
    </tr>)}</tbody>
  </table>
</div>;

const EmployeeTable = ({ items, summary, expanded, onToggle }) => <div className='bc_ps_table_wrap'>
  <table className='bc_ps_table bc_ps_employee_table'>
    <caption>Bán hàng theo nhân viên; mở từng nhân viên để xem hàng hóa</caption>
    <thead><tr><th scope='col'>Nhân viên</th><th scope='col' className='is-number'>Số mặt hàng</th><MetricHeaders /></tr></thead>
    <tbody><tr className='bc_ps_total_row'><th scope='row' colSpan={2}>Tổng cộng toàn bộ kết quả lọc</th><MetricCells row={summary} /></tr>{items.map((person) => <React.Fragment key={person.username}>
      <tr><th scope='row' className='is-employee'>
        <button type='button' className='bc_ps_expand' aria-expanded={expanded.has(person.username)}
          aria-controls={'bc-ps-products-' + person.username} onClick={() => onToggle(person.username)}>
          {expanded.has(person.username) ? <FiChevronDown /> : <FiChevronRight />}{employeeLabel(person)}
        </button>
      </th><td className='is-number'>{formatProductSalesNumber(person.product_count)}</td><MetricCells row={person} /></tr>
      {expanded.has(person.username) ? <tr className='bc_ps_detail_row'><td colSpan={7} id={'bc-ps-products-' + person.username}>
        {(person.products || []).length ? <ProductTable items={person.products} summary={person}
          caption={'Hàng hóa của ' + employeeLabel(person)} /> : <p>Không có chi tiết hàng hóa trong kỳ.</p>}
      </td></tr> : null}
    </React.Fragment>)}</tbody>
  </table>
</div>;

const ProductSalesReport = ({ employee = false }) => {
  const history = useHistory();
  const location = useLocation();
  const { verifyPermission } = useVerifyAccess();
  const canExport = verifyPermission('SL_SALES_REPORT_EXPORT');
  const reportId = employee ? 'employee-product-sales' : 'product-sales';
  const title = employee ? 'Bán hàng theo nhân viên và hàng hóa' : 'Bán hàng theo hàng hóa';
  const query = useMemo(() => getProductSalesQuery(location.search), [location.search]);
  const [scope, setScope] = useState(getPortalScope);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printReport, setPrintReport] = useState(null);
  const [printContext, setPrintContext] = useState(null);
  const printRef = useRef(0);
  const printInvokedRef = useRef(null);
  const [error, setError] = useState('');
  const [exportError, setExportError] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const requestRef = useRef(0);
  const exportRef = useRef(0);
  const contextKey = JSON.stringify([reportId, scope.company_id, query]);
  const contextRef = useRef(contextKey);
  contextRef.current = contextKey;
  const [loadedContext, setLoadedContext] = useState(null);
  const currentReport = loadedContext === contextKey ? data : null;
  const apply = useCallback((values) => {
    const params = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => { if (String(value ?? '').trim()) params.set(key, value); });
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [history, location.pathname]);

  useEffect(() => {
    const changed = (event) => {
      const next = event?.detail || getPortalScope();
      exportRef.current += 1; printRef.current += 1;
      setExporting(false); setPrinting(false); setPrintReport(null);
      setScope(next);
      if (String(next.company_id || '') !== String(scope.company_id || '')) {
        apply({ ...query, business_id: '', store_id: '', salesperson: '', price_list_id: '', page: 1 });
      }
    };
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, changed);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, changed);
  }, [scope.company_id, query, apply]);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    const requestedContext = contextKey;
    const isCurrent = () => requestId === requestRef.current && contextRef.current === requestedContext;
    setData(null); setLoadedContext(null); setExpanded(new Set()); setError('');
    if (!scope.company_id) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await (employee ? getEmployeeProductSalesReport : getProductSalesReport)(query);
      if (isCurrent()) { setData(response); setLoadedContext(requestedContext); }
    } catch (requestError) {
      if (isCurrent()) setError(requestError?.message || 'Không thể tải báo cáo bán hàng.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [employee, query, scope.company_id, contextKey]);
  useEffect(() => {
    exportRef.current += 1; printRef.current += 1;
    setExporting(false); setPrinting(false); setPrintReport(null); setExportError(''); load();
    return () => { requestRef.current += 1; exportRef.current += 1; printRef.current += 1; };
  }, [load]);

  useEffect(() => {
    const cleanup = () => document.body.classList.remove('bc_ps_printing');
    window.addEventListener('afterprint', cleanup);
    return () => { cleanup(); window.removeEventListener('afterprint', cleanup); };
  }, []);
  const exportExcel = async () => {
    if (!canExport || exporting || !currentReport || loading) return;
    const exportId = ++exportRef.current;
    const exportContext = contextKey;
    const isCurrent = () => exportId === exportRef.current && contextRef.current === exportContext;
    setExporting(true); setExportError('');
    try {
      const response = await (employee ? getEmployeeProductSalesExport : getProductSalesExport)(query);
      if (!isCurrent()) return;
      const workbook = buildProductSalesWorkbook(response, { employee, query });
      XLSX.writeFile(workbook, reportId + '-' + query.date_from + '-' + query.date_to + '.xlsx');
    } catch (requestError) {
      if (isCurrent()) setExportError(requestError?.message || 'Không thể xuất Excel.');
    } finally {
      if (isCurrent()) setExporting(false);
    }
  };
  const printAll = async () => {
    if (!canExport || printing || !currentReport || loading) return;
    const printId = ++printRef.current;
    const requestedContext = contextKey;
    const isCurrent = () => printId === printRef.current && contextRef.current === requestedContext;
    setPrinting(true); setPrintReport(null); setExportError('');
    try {
      const response = await (employee ? getEmployeeProductSalesExport : getProductSalesExport)(query);
      if (!isCurrent()) return;
      setPrintContext(requestedContext); setPrintReport(response);
    } catch (requestError) {
      if (isCurrent()) { setPrinting(false); setExportError(requestError?.message || 'Không thể chuẩn bị bản in.'); }
    }
  };
  useEffect(() => {
    if (!printReport || printContext !== contextKey || printInvokedRef.current === printReport) return;
    printInvokedRef.current = printReport;
    const frame = window.requestAnimationFrame(() => {
      if (contextRef.current !== printContext) return;
      document.body.classList.add('bc_ps_printing');
      window.print(); setPrinting(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [printReport, printContext, contextKey]);
  const toggle = (username) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(username)) next.delete(username); else next.add(username);
    return next;
  });
  const items = currentReport?.items || [];
  const pagination = currentReport?.pagination || {};
  const options = currentReport?.filter_options || {};
  const total = Number(pagination.total_items) || 0;
  const page = Number(pagination.page) || query.page;
  const pages = Math.max(1, Number(pagination.total_pages) || 1);
  const selectedStore = (options.stores || []).find((store) => String(store.store_id) === String(query.store_id));
  const selectedBusiness = (options.stores || []).find((store) => String(store.business_id) === String(query.business_id));

  return <ReportPageLayout reportId={reportId}>
    <div className='bc_ps_report_page'><div className='bc_ps_screen_report'>
      <ReportPageHeader title={title} description='Bán khách (bán lẻ và bán sỉ); doanh thu ghi nhận trước VAT.'
        actions={<button type='button' className='bc_rr_action_button is-secondary' onClick={load} disabled={loading || !scope.company_id}>
          <FiRefreshCw /> Làm mới
        </button>} />
      <ReportContextBar items={[
        { label: 'Kỳ', value: query.date_from + ' – ' + query.date_to },
        { label: 'Phạm vi', value: selectedStore?.store_name || selectedBusiness?.business_name || 'Toàn bộ cửa hàng được phân quyền trong công ty' },
      ]} />
      <ProductSalesReportFilters query={query} options={options} employee={employee} loading={loading}
        meta={currentReport?.meta || {}} onApply={apply} />
      <ReportDiagnostics meta={currentReport?.meta} />
      <p className='bc_ps_note'>{employee
        ? 'Số lượng, doanh thu và trả hàng chia đều cho các nhân viên bán hàng trên đơn. Tổng cộng lấy toàn bộ kết quả lọc.'
        : 'Giảm giá đơn và giá trị trả hàng đã được phân bổ theo hàng hóa. Tổng cộng lấy toàn bộ kết quả lọc.'}</p>
      {(currentReport?.meta?.notices || []).length ? <details className='bc_ps_source_notes'><summary>Quy tắc và đối chiếu số liệu</summary>{currentReport.meta.notices.map((notice, index) => <p className='bc_ps_notice' key={index}>{notice}</p>)}</details> : null}
      <ReportTableToolbar summary={<strong>{formatProductSalesNumber(total)} {employee ? 'nhân viên' : 'mặt hàng'}</strong>}
        actions={<>
          {canExport ? <button type='button' className='is-secondary' disabled={!currentReport || loading || printing || !items.length}
            onClick={printAll}><FiPrinter /> {printing ? 'Đang chuẩn bị bản in' : 'In toàn bộ báo cáo'}</button> : null}
          {canExport ? <button type='button' className='is-primary' disabled={!currentReport || loading || exporting || !items.length}
            onClick={exportExcel}><FiDownload />{exporting ? 'Đang xuất Excel' : 'Xuất Excel'}</button> : null}
        </>} />
      {exportError ? <ReportState type='error'>{exportError}</ReportState> : null}
      {error ? <ReportState type='error'>{error}</ReportState> : null}
      {loading ? <ReportState type='loading'>Đang tổng hợp báo cáo bán hàng.</ReportState> : null}
      {!loading && !error && !scope.company_id ? <ReportState>Chọn công ty ở thanh trên cùng để xem báo cáo.</ReportState> : null}
      {!loading && !error && currentReport && !items.length ? <ReportState>Không có bán hàng hoặc trả hàng trong kỳ và phạm vi đã chọn.</ReportState> : null}
      {!loading && !error && items.length ? <>
        {employee ? <EmployeeTable items={items} summary={currentReport.summary || {}} expanded={expanded} onToggle={toggle} />
          : <ProductTable items={items} summary={currentReport.summary || {}} caption={title} />}
        <div className='bc_ps_pagination'><span>Trang {page}/{pages} · {formatProductSalesNumber(total)} {employee ? 'nhân viên' : 'mặt hàng'}</span>
          <div><button type='button' disabled={page <= 1} onClick={() => apply({ ...query, page: page - 1 })}>Trang trước</button>
            <button type='button' disabled={page >= pages} onClick={() => apply({ ...query, page: page + 1 })}>Trang sau</button></div>
        </div>
      </> : null}
    </div>
    {printReport && printContext === contextKey ? <section className='bc_ps_print_report' aria-hidden='true'>
      <h2>{title}</h2><p>{query.date_from} – {query.date_to} · Bán khách (bán lẻ và bán sỉ); doanh thu trước VAT.</p>
      <p>{selectedStore?.store_name || selectedBusiness?.business_name || 'Toàn bộ cửa hàng được phân quyền trong công ty'}</p>
      <ReportDiagnostics meta={printReport.meta} />
      {employee ? <EmployeeTable items={printReport.items || []} summary={printReport.summary || {}}
        expanded={new Set((printReport.items || []).map((person) => person.username))} onToggle={() => {}} />
        : <ProductTable items={printReport.items || []} summary={printReport.summary || {}} caption={title} />}
    </section> : null}
    </div>
  </ReportPageLayout>;
};

export const EmployeeProductSalesReportPage = () => <ProductSalesReport employee />;
export const ProductSalesReportPage = () => <ProductSalesReport />;
export default ProductSalesReportPage;
